import { useMemo, useState } from "react";
import {
	Box,
	TextField,
	Typography,
	useMediaQuery,
	InputAdornment,
	IconButton,
	Stack,
	Divider,
	Chip,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import MailRoundedIcon from "@mui/icons-material/MailRounded";

import { Formik, getIn } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";

import { authUser } from "../../utils/firebase/firebase";
import { setIsAuthUser, setUser } from "../../store/user/user.action";

import PasswordReset from "./password-reset";

const LOGO_SRC = "Logo-rosa.png";

const initialValues = {
	username: "",
	password: "",
};

const userSchema = yup.object().shape({
	username: yup
		.string()
		.email("Informe um e-mail válido")
		.required("Informe seu e-mail"),
	password: yup
		.string()
		.min(5, "Senha muito curta")
		.required("Informe sua senha"),
});

const Auth = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const isMobile = useMediaQuery("(max-width: 700px)");
	const isSmallHeight = useMediaQuery("(max-height: 720px)");

	const [isLoading, setIsLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const palette = useMemo(
		() => ({
			pageBg:
				"radial-gradient(circle at top left, rgba(244, 114, 182, 0.14), transparent 28%), radial-gradient(circle at bottom right, rgba(251, 207, 232, 0.30), transparent 34%), linear-gradient(135deg, #fffdfd 0%, #fbf5f7 48%, #f4ecef 100%)",
			cardBg: "rgba(255, 255, 255, 0.78)",
			cardBorder: "rgba(255, 255, 255, 0.92)",
			panelBg:
				"linear-gradient(145deg, rgba(255,255,255,0.96), rgba(255,244,248,0.88))",
			textPrimary: "#24141d",
			textSecondary: "rgba(36, 20, 29, 0.64)",
			fieldBg: "rgba(255,255,255,0.92)",
			fieldBorder: "rgba(36,20,29,0.10)",
			fieldHover: "rgba(36,20,29,0.18)",
			primary: "#df4b86",
			primaryDark: "#bc2f68",
			primarySoft: "rgba(223,75,134,0.10)",
			logoCardBg: "rgba(255,255,255,0.92)",
			logoCardBorder: "rgba(36,20,29,0.06)",
			forgotColor: "#9a5a00",
			forgotHover: "#7d4700",
		}),
		[]
	);

	const handleFormSubmit = async (values) => {
		setIsLoading(true);

		try {
			const email = String(values.username || "").toLowerCase().trim();
			const password = values.password;

			const user = await authUser(email, password);

			if (user) {
				dispatch(setIsAuthUser(true));
				dispatch(setUser(user.user));
				toast.success("Login efetuado com sucesso!");
				navigate("/");
			}
		} catch (error) {
			console.log("Erro ao fazer login:", error);

			const errorMessage =
				error?.message ||
				error?.code ||
				"Não foi possível entrar. Verifique seus dados e tente novamente.";

			toast.error(`Erro: ${errorMessage}`);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100dvh",
				width: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				px: isMobile ? 2 : 4,
				py: isSmallHeight ? 3 : 6,
				background: palette.pageBg,
				position: "relative",
				overflow: "hidden",
			}}
		>
			<Box
				sx={{
					position: "absolute",
					width: 420,
					height: 420,
					borderRadius: "50%",
					background:
						"linear-gradient(135deg, rgba(223,75,134,0.10), rgba(255,255,255,0.02))",
					filter: "blur(24px)",
					top: -170,
					right: -120,
					pointerEvents: "none",
				}}
			/>

			<Box
				sx={{
					position: "absolute",
					width: 320,
					height: 320,
					borderRadius: "50%",
					background:
						"linear-gradient(135deg, rgba(255,255,255,0.80), rgba(223,75,134,0.10))",
					filter: "blur(24px)",
					bottom: -120,
					left: -90,
					pointerEvents: "none",
				}}
			/>

			<Box
				sx={{
					width: "100%",
					maxWidth: 1040,
					display: "grid",
					gridTemplateColumns: isMobile ? "1fr" : "1.03fr 0.97fr",
					borderRadius: isMobile ? "28px" : "36px",
					background: palette.cardBg,
					border: `1px solid ${palette.cardBorder}`,
					boxShadow: "0 28px 80px rgba(95, 52, 72, 0.12)",
					backdropFilter: "blur(24px)",
					overflow: "hidden",
					position: "relative",
					zIndex: 1,
				}}
			>
				<Box
					sx={{
						display: isMobile ? "none" : "flex",
						flexDirection: "column",
						justifyContent: "space-between",
						p: 5,
						minHeight: 620,
						background: palette.panelBg,
						color: palette.textPrimary,
						position: "relative",
						overflow: "hidden",
						borderRight: "1px solid rgba(36,20,29,0.05)",
					}}
				>
					<Box
						sx={{
							position: "absolute",
							inset: 0,
							background:
								"radial-gradient(circle at 24% 18%, rgba(223,75,134,0.10), transparent 30%), radial-gradient(circle at 92% 78%, rgba(255,255,255,0.86), transparent 36%)",
							pointerEvents: "none",
						}}
					/>

					<Box sx={{ position: "relative", zIndex: 1 }}>
						<Chip
							label="Área restrita"
							sx={{
								height: 34,
								px: 1,
								color: palette.primaryDark,
								fontWeight: 800,
								letterSpacing: 0.2,
								background: palette.primarySoft,
								border: "1px solid rgba(223,75,134,0.14)",
								backdropFilter: "blur(10px)",
							}}
						/>

						<Typography
							variant="h1"
							sx={{
								mt: 3,
								fontSize: "clamp(40px, 5vw, 62px)",
								lineHeight: 0.98,
								fontWeight: 900,
								letterSpacing: "-2.3px",
								color: palette.textPrimary,
							}}
						>
							Gestão mais leve.
							<br />
							Vendas mais claras.
						</Typography>

						<Typography
							sx={{
								mt: 3,
								maxWidth: 405,
								fontSize: 17,
								lineHeight: 1.7,
								color: palette.textSecondary,
							}}
						>
							Acesse o painel para acompanhar kits, cobranças,
							vendedoras e operações da Pitaya Joias com mais
							organização e fluidez.
						</Typography>
					</Box>

					<Box
						sx={{
							position: "relative",
							zIndex: 1,
							width: "100%",
							display: "flex",
							alignItems: "flex-end",
							justifyContent: "space-between",
							gap: 3,
							mt: 5,
							overflow: "visible",
						}}
					>
						<Stack
							direction="row"
							spacing={1.5}
							sx={{
								alignItems: "center",
								pb: 1.2,
							}}
						>
							<Box
								sx={{
									width: 9,
									height: 9,
									borderRadius: "50%",
									background: palette.primary,
								}}
							/>
							<Box
								sx={{
									width: 9,
									height: 9,
									borderRadius: "50%",
									background: "rgba(223,75,134,0.35)",
								}}
							/>
							<Box
								sx={{
									width: 9,
									height: 9,
									borderRadius: "50%",
									background: "rgba(223,75,134,0.16)",
								}}
							/>
						</Stack>

						<Box
							component="img"
							src={LOGO_SRC}
							alt="Pitaya Joias"
							sx={{
								width: "clamp(175px, 43%, 250px)",
								height: "auto",
								maxHeight: 118,
								objectFit: "contain",
								objectPosition: "right bottom",
								display: "block",
								flexShrink: 0,
								filter: "drop-shadow(0 14px 22px rgba(95, 52, 72, 0.10))",

								// compensa o espaço transparente/branco dentro do PNG
								transform: "translate(8px, 18px)",
							}}
						/>
					</Box>
				</Box>

				<Box
					sx={{
						p: isMobile ? 3 : 5,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background:
							"linear-gradient(180deg, rgba(255,255,255,0.84), rgba(255,255,255,0.68))",
					}}
				>
					<Box
						sx={{
							width: "100%",
							maxWidth: 420,
						}}
					>
						{isMobile && (
							<Box
								sx={{
									width: "100%",
									display: "flex",
									justifyContent: "flex-end",
									alignItems: "center",
									mb: 4,
									pr: 0.5,
								}}
							>
								<Box
									component="img"
									src={LOGO_SRC}
									alt="Pitaya Joias"
									sx={{
										width: "min(210px, 64vw)",
										height: "auto",
										maxHeight: 104,
										objectFit: "contain",
										objectPosition: "right center",
										display: "block",
										filter: "drop-shadow(0 12px 20px rgba(95, 52, 72, 0.10))",
									}}
								/>
							</Box>
						)}

						<Box sx={{ mb: 4 }}>
							<Typography
								sx={{
									fontSize: isMobile ? 34 : 40,
									lineHeight: 1,
									fontWeight: 900,
									letterSpacing: "-1.6px",
									color: palette.textPrimary,
								}}
							>
								Entrar
							</Typography>

							<Typography
								sx={{
									mt: 1.4,
									fontSize: 15.5,
									lineHeight: 1.6,
									color: palette.textSecondary,
								}}
							>
								Informe seus dados para acessar o painel.
							</Typography>
						</Box>

						<Formik
							initialValues={initialValues}
							validationSchema={userSchema}
							validateOnMount
							onSubmit={async (values, actions) => {
								await handleFormSubmit(values);
								actions.setSubmitting(false);
							}}
						>
							{({
								values,
								errors,
								touched,
								handleBlur,
								handleChange,
								handleSubmit,
								setFieldValue,
								isValid,
								dirty,
							}) => (
								<Box component="form" onSubmit={handleSubmit} noValidate>
									<Stack spacing={2.2}>
										<TextField
											fullWidth
											name="username"
											label="E-mail"
											type="email"
											value={values.username}
											onBlur={(event) => {
												handleBlur(event);
												setFieldValue(
													"username",
													String(event.target.value || "")
														.toLowerCase()
														.trim()
												);
											}}
											onChange={(event) => {
												const value = String(event.target.value || "")
													.toLowerCase()
													.trimStart();
												setFieldValue("username", value);
											}}
											error={Boolean(
												getIn(touched, "username") &&
												getIn(errors, "username")
											)}
											helperText={
												getIn(touched, "username") &&
												getIn(errors, "username")
											}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<MailRoundedIcon
															sx={{
																fontSize: 20,
																color: palette.textSecondary,
															}}
														/>
													</InputAdornment>
												),
											}}
											sx={fieldSx(palette)}
										/>

										<TextField
											fullWidth
											name="password"
											label="Senha"
											type={showPassword ? "text" : "password"}
											value={values.password}
											onBlur={handleBlur}
											onChange={handleChange}
											error={Boolean(
												getIn(touched, "password") &&
												getIn(errors, "password")
											)}
											helperText={
												getIn(touched, "password") &&
												getIn(errors, "password")
											}
											InputProps={{
												startAdornment: (
													<InputAdornment position="start">
														<LockRoundedIcon
															sx={{
																fontSize: 20,
																color: palette.textSecondary,
															}}
														/>
													</InputAdornment>
												),
												endAdornment: (
													<InputAdornment position="end">
														<IconButton
															type="button"
															edge="end"
															onClick={() =>
																setShowPassword((current) => !current)
															}
															sx={{
																color: palette.textSecondary,
															}}
														>
															{showPassword ? (
																<VisibilityOffRoundedIcon />
															) : (
																<VisibilityRoundedIcon />
															)}
														</IconButton>
													</InputAdornment>
												),
											}}
											sx={fieldSx(palette)}
										/>

										<LoadingButton
											loading={isLoading}
											type="submit"
											variant="contained"
											disabled={!dirty || !isValid || isLoading}
											sx={{
												mt: 0.5,
												height: 52,
												borderRadius: "999px",
												textTransform: "none",
												fontSize: 16,
												fontWeight: 800,
												letterSpacing: "-0.2px",
												color: "#fff",
												background:
													"linear-gradient(135deg, #df4b86 0%, #cf3f78 45%, #b92d65 100%)",
												boxShadow:
													"0 16px 30px rgba(223, 75, 134, 0.22)",
												"&:hover": {
													background:
														"linear-gradient(135deg, #e75590 0%, #d8437d 45%, #c1326c 100%)",
													boxShadow:
														"0 18px 36px rgba(223, 75, 134, 0.28)",
												},
												"&.Mui-disabled": {
													color: "rgba(255,255,255,0.70)",
													background:
														"linear-gradient(135deg, rgba(223,75,134,0.42), rgba(185,45,101,0.42))",
												},
											}}
										>
											Entrar na conta
										</LoadingButton>

										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												pt: 0.5,
												"& button, & a, & .MuiButton-root, & .MuiTypography-root": {
													color: `${palette.forgotColor} !important`,
													fontWeight: "800 !important",
													textTransform: "none !important",
												},
												"& button:hover, & a:hover, & .MuiButton-root:hover": {
													color: `${palette.forgotHover} !important`,
													backgroundColor: "transparent !important",
												},
											}}
										>
											<PasswordReset />
										</Box>
									</Stack>
								</Box>
							)}
						</Formik>

						<Divider
							sx={{
								my: 3.5,
								borderColor: "rgba(36,20,29,0.08)",
							}}
						/>

						<Typography
							sx={{
								textAlign: "center",
								fontSize: 13,
								lineHeight: 1.6,
								color: palette.textSecondary,
							}}
						>
							Acesso seguro ao painel Pitaya Joias.
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

const fieldSx = (palette) => ({
	"& .MuiOutlinedInput-root": {
		height: 56,
		borderRadius: "18px",
		backgroundColor: palette.fieldBg,
		backdropFilter: "blur(12px)",
		transition: "all 180ms ease",
		boxShadow: "0 10px 22px rgba(95,52,72,0.04)",
		"& fieldset": {
			borderColor: palette.fieldBorder,
		},
		"&:hover fieldset": {
			borderColor: palette.fieldHover,
		},
		"&.Mui-focused fieldset": {
			borderColor: palette.primary,
			borderWidth: "1.5px",
		},
		"&.Mui-error fieldset": {
			borderColor: "#ef4444",
		},
	},
	"& .MuiInputLabel-root": {
		color: palette.textSecondary,
		fontWeight: 650,
	},
	"& .MuiInputLabel-root.Mui-focused": {
		color: palette.primary,
	},
	"& .MuiOutlinedInput-input": {
		color: palette.textPrimary,
		fontWeight: 650,
	},
	"& .MuiFormHelperText-root": {
		marginLeft: "14px",
		fontWeight: 650,
	},
	"& input:-webkit-autofill": {
		WebkitBoxShadow: `0 0 0 100px ${palette.fieldBg} inset !important`,
		WebkitTextFillColor: `${palette.textPrimary} !important`,
		caretColor: palette.textPrimary,
		borderRadius: "18px",
	},
});

export default Auth;